"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Link from "next/link";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Banner 1058 — KUK home "Want to set yourself apart" band.
 *
 * Port of kuk-campus-portal `components/Home/ContactUs.tsx`: a short full-bleed
 * photograph under a black scrim, carrying a small icon, one bold line of copy
 * and a white outline CTA, all left-aligned and spread down the band.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as
 * `tab:`.
 *
 * THE ICON SIZE — v1 ships the icon twice, `hidden md:block` at 40px and
 * `md:hidden` at 30px. Rendered once here with the size switched in CSS.
 *
 * THE SCRIM — v1 writes `bg-[#000000] bg-opacity-50` over the photo, i.e. black
 * at 50%. That is a single CMS colour here (`bleed_background_color`), so it can
 * be tuned or removed without touching the artwork.
 *
 * NOT CARRIED OVER — v1's `isUserVerified` branch, which swaps the CTA for a
 * "Continue Application" link to the admission portal. That is per-visitor
 * session state, not page content; an editor who wants the link CTA turns
 * `apply_now_is_cta` off and sets `apply_now_link`.
 *
 * THE ASSET PATHS — v1 references `images/hero.png` and `icons/note.svg` as
 * RELATIVE URLs, so they resolve against the current route and 404 on any nested
 * page. Both are CMS fields here.
 */

interface Banner1058Data {
  /** Background photograph. */
  background_image?: string;
  /** Optional; falls back to the desktop image. */
  background_image_mobile?: string;
  /** Scrim over the photo. */
  bleed_background_color?: string;
  /** Band height in px. */
  hero_section_height?: number;
  /** Icon-map key or an uploaded image URL. */
  icon?: string;
  alt_icon?: string;
  icon_color?: string;
  title?: string;
  title_color?: string;
  apply_now_show?: boolean;
  apply_now_text?: string;
  apply_now_link?: string;
  apply_now_is_cta?: boolean;
  apply_now_color?: string;
  apply_now_border_color?: string;
  status?: boolean;
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

const Banner1058 = (props: { data?: Banner1058Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Banner1058Data | undefined;
  const dispatch = useDispatch();

  const {
    background_image = "",
    background_image_mobile = "",
    bleed_background_color = "rgba(0, 0, 0, 0.5)",
    hero_section_height = 285,
    icon = "",
    alt_icon = "",
    icon_color = "#FFFFFF",
    title = "",
    title_color = "#FFFFFF",
    apply_now_text = "Get in touch",
    apply_now_link = "",
    apply_now_color = "#FFFFFF",
    apply_now_border_color = "#FFFFFF",
  } = sectionData || {};

  const applyNowShow = isCmsShowFlagOn(sectionData?.apply_now_show, true);
  const applyNowIsCta = isCmsShowFlagOn(sectionData?.apply_now_is_cta, true);
  const status = isCmsShowFlagOn(sectionData?.status, true);

  const handleApplyNowClick = useCallback(() => {
    ctaClick({
      section: "banner_1058",
      component: "get_in_touch",
      label: apply_now_text?.trim() || "get_in_touch",
      step: "1",
      entity: "banner_1058",
      parentEntity: "banner_1058",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [apply_now_text, dispatch]);

  if (!status) return null;

  const desktopBg = background_image || background_image_mobile;
  const mobileBg = background_image_mobile || background_image;

  const ctaClassName = cn(
    "inline-flex h-10 w-[152px] items-center justify-center rounded-md border",
    "bg-transparent px-5 text-sm font-medium shadow-sm transition-colors",
  );
  const ctaStyle = {
    color: apply_now_color,
    borderColor: apply_now_border_color,
  };

  return (
    <section
      className={cn(
        "w-full bg-cover bg-center bg-no-repeat",
        "[background-image:var(--kuk-band-mobile)] tab:[background-image:var(--kuk-band-desktop)]",
      )}
      style={{
        height: `${hero_section_height}px`,
        ["--kuk-band-mobile" as string]: mobileBg ? `url("${mobileBg}")` : "none",
        ["--kuk-band-desktop" as string]: desktopBg ? `url("${desktopBg}")` : "none",
      }}
    >
      <div
        className="flex size-full flex-col items-start justify-around p-10 tab:px-20"
        style={{ backgroundColor: bleed_background_color }}
      >
        {icon && (
          <span
            className="[&_img]:size-[30px] [&_svg]:size-[30px] tab:[&_img]:size-[40px] tab:[&_svg]:size-[40px]"
            aria-label={alt_icon || undefined}
          >
            <DynamicIcon
              iconName={icon}
              color={icon_color}
              width="40"
              height="40"
            />
          </span>
        )}

        {title && (
          <h2
            className="my-5 w-2/3 text-xl font-bold tab:text-[32px]"
            style={{ color: title_color }}
          >
            {title}
          </h2>
        )}

        {applyNowShow &&
          apply_now_text &&
          (applyNowIsCta || !apply_now_link ? (
            <button
              type="button"
              onClick={handleApplyNowClick}
              className={ctaClassName}
              style={ctaStyle}
            >
              {apply_now_text}
            </button>
          ) : (
            <Link
              href={normalizeHref(apply_now_link)}
              className={ctaClassName}
              style={ctaStyle}
            >
              {apply_now_text}
            </Link>
          ))}
      </div>
    </section>
  );
};

export default Banner1058;
